import { keccak256, encodeAbiParameters, toBytes } from 'viem';

export const CASE_STATUSES = ['received', 'in review', 'needs information', 'on hold', 'escalated', 'contested', 'approved', 'denied', 'closed'];
export const ZERO_HASH = `0x${'0'.repeat(64)}`;

const h = (s) => keccak256(toBytes(s || ''));
const TYPES = ['bytes32', 'bytes32', 'uint32', 'bytes32', 'bytes32', 'bytes32', 'uint64', 'uint64', 'bytes32', 'bytes32', 'bytes32'].map((type) => ({ type }));

export function eventHash(e) {
  return keccak256(encodeAbiParameters(TYPES, [
    e.claimId, h(e.caseRef), Number(e.eventIndex), e.prevEventHash, h(e.status), h(e.missingInfo),
    BigInt(e.updatedAt), BigInt(e.nextUpdateBy || 0), h(e.decisionCode), h(e.refundRoute), e.noteHash || ZERO_HASH,
  ]));
}

const fmt = (t) => { const d = new Date(Number(t) * 1000); const p = (n) => String(n).padStart(2, '0'); return `${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}-${d.getUTCFullYear()} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`; };

function linkCheck(events) {
  for (let i = 0; i < events.length; i += 1) {
    const e = events[i];
    if (Number(e.eventIndex) !== i + 1) return `Event in position ${i + 1} has eventIndex ${e.eventIndex}. An event is missing or out of order.`;
    const want = i === 0 ? ZERO_HASH : eventHash(events[i - 1]);
    if ((e.prevEventHash || '').toLowerCase() !== want) {
      return i === 0 ? 'The first event must have a zero prevEventHash.' : `Event ${i + 1} does not link to event ${i}. An earlier event was changed, removed, or reordered.`;
    }
  }
  return null;
}

const B32 = /^0x[0-9a-fA-F]{64}$/;
const isUint = (v) => (typeof v === 'number' || (typeof v === 'string' && v.trim() !== '')) && Number.isInteger(Number(v)) && Number(v) >= 0;

function shapeError(e, i) {
  const n = `Event ${i + 1}`;
  if (!e || typeof e !== 'object' || Array.isArray(e)) return `${n} is not an object.`;
  if (!B32.test(e.claimId || '')) return `${n}: claimId must be 0x followed by 64 hex characters.`;
  if (!B32.test(e.prevEventHash || '')) return `${n}: prevEventHash must be 0x followed by 64 hex characters.`;
  if (e.noteHash && !B32.test(e.noteHash)) return `${n}: noteHash must be 0x followed by 64 hex characters.`;
  if (!isUint(e.eventIndex) || !isUint(e.updatedAt)) return `${n}: eventIndex and updatedAt must be whole numbers.`;
  if (e.nextUpdateBy !== undefined && !isUint(e.nextUpdateBy)) return `${n}: nextUpdateBy must be a whole number of seconds.`;
  if (typeof e.caseRef !== 'string' || typeof e.status !== 'string') return `${n}: caseRef and status must be text.`;
  return null;
}

export function verifyTimeline(input, nowSec = Math.floor(Date.now() / 1000)) {
  const events = Array.isArray(input) ? input : input?.events;
  if (!Array.isArray(events) || !events.length) throw new Error('Paste a JSON array of case events, or an object with an "events" array.');
  const bad = events.map(shapeError).find(Boolean);
  if (bad) throw new Error(bad);
  const sameClaim = events.every((e) => e.claimId === events[0].claimId);
  const sameCase = events.every((e) => e.caseRef === events[0].caseRef);
  const linkErr = linkCheck(events);
  const timesOk = events.every((e, i) => i === 0 || Number(e.updatedAt) >= Number(events[i - 1].updatedAt));
  const badStatus = events.find((e) => !CASE_STATUSES.includes(e.status));
  const last = events[events.length - 1];
  const open = last.status !== 'closed';
  const overdue = open && Number(last.nextUpdateBy) > 0 && Number(last.nextUpdateBy) < nowSec;
  const checks = [
    { id: 'same-claim', label: 'One claim', ok: sameClaim, detail: sameClaim ? 'Every event has the same claimId.' : 'Events carry different claimIds.' },
    { id: 'same-case', label: 'One case', ok: sameCase, detail: sameCase ? 'Every event has the same case reference.' : 'Events carry different case references.' },
    { id: 'chain', label: 'Nothing removed or reordered', ok: !linkErr, detail: linkErr || `All ${events.length} events are in order and each links to the one before it.` },
    { id: 'times', label: 'Times move forward', ok: timesOk, detail: timesOk ? 'Each update is at or after the one before it.' : 'An update is dated earlier than the one before it.' },
    { id: 'status', label: 'Known status values', ok: !badStatus, detail: badStatus ? `"${badStatus.status}" is not on the status list.` : 'Every status is on the published list.' },
  ];
  const deadline = overdue
    ? { overdue: true, detail: `Overdue. The last event promised an update by ${fmt(last.nextUpdateBy)}, and no newer event is in this copy.` }
    : { overdue: false, detail: !open ? 'The case is closed.' : Number(last.nextUpdateBy) > 0 ? `Not overdue. Next update due by ${fmt(last.nextUpdateBy)}.` : 'Not overdue. The last event sets no next update time.' };
  return { checks, deadline, ok: checks.every((c) => c.ok), count: events.length, latestHash: eventHash(last), lastStatus: last.status };
}
